/**
 * Die Tab-Leiste am unteren Rand.
 *
 * Sie steht dort, wo der Daumen ohnehin liegt, und sie steht immer: Ein
 * Wechsel zwischen Liste, Karte, Statistik und Check-in ist ein Tippen, kein
 * Weg über ein Menü. Vier Ziele plus „Mehr" — die Aufteilung begründet
 * `navigation.js`.
 *
 * Auf breiten Schirmen wird sie nicht gezeichnet; dort trägt die Kopfzeile
 * alle acht Ziele nebeneinander, und daran ändert dieser Sprint nichts.
 */

import { HAUPTZIELE, WEITERE_ZIELE, zielFuer } from '../navigation.js';
import { stups } from '../mobil.js';
import { symbol } from '../symbole.js';
import { oeffneBlatt } from './blatt.js';

/** Ein Eintrag: Zeichen oben, Wort darunter — beides gehört dazu. */
function eintrag(ziel) {
  const a = document.createElement('a');
  a.className = 'tab';
  a.href = `#${ziel.pfad}`;
  a.dataset.pfad = ziel.pfad;
  a.append(symbol(ziel.symbol));
  const wort = document.createElement('span');
  wort.className = 'tab-wort';
  wort.textContent = ziel.titel;
  a.append(wort);
  return a;
}

/**
 * Das „Mehr"-Blatt: die vier übrigen Ziele, gross genug zum Treffen.
 *
 * Als Blatt und nicht als Ausklappmenü, weil ein Menü, das über der Leiste
 * schwebt, auf kleinen Schirmen fast immer am falschen Ende landet.
 */
async function zeigeMehr(aktiverPfad) {
  const ziel = await oeffneBlatt({
    titel: 'Mehr',
    klasse: 'mehrblatt',
    aufbauen: (schliesse) => {
      const liste = document.createElement('nav');
      liste.className = 'mehrliste';
      for (const z of WEITERE_ZIELE) {
        const a = document.createElement('a');
        a.className = z.pfad === aktiverPfad ? 'mehrziel aktiv' : 'mehrziel';
        a.href = `#${z.pfad}`;
        a.append(symbol(z.symbol, 22));
        const wort = document.createElement('span');
        wort.textContent = z.titel;
        a.append(wort);
        a.addEventListener('click', () => schliesse(z.pfad));
        liste.append(a);
      }
      return liste;
    },
  });
  return ziel;
}

export function tableiste() {
  const leiste = document.createElement('nav');
  leiste.className = 'tableiste';
  leiste.setAttribute('aria-label', 'Hauptbereiche');

  for (const ziel of HAUPTZIELE) leiste.append(eintrag(ziel));

  const mehr = document.createElement('button');
  mehr.type = 'button';
  mehr.className = 'tab tab-mehr';
  mehr.append(symbol('mehr'));
  const wort = document.createElement('span');
  wort.className = 'tab-wort';
  wort.textContent = 'Mehr';
  mehr.append(wort);
  mehr.addEventListener('click', () => {
    stups();
    zeigeMehr(leiste.dataset.aktiv ?? '/');
  });
  leiste.append(mehr);

  // Ein Tippen auf den bereits aktiven Reiter führt nach oben — dieselbe
  // Gewohnheit wie in jeder App, und der einzige schnelle Weg zurück an den
  // Anfang einer Liste mit 995 Zeilen.
  leiste.addEventListener('click', (e) => {
    const tab = e.target.closest?.('.tab[data-pfad]');
    if (!tab || tab.dataset.pfad !== leiste.dataset.aktiv) return;
    globalThis.scrollTo({ top: 0, behavior: 'smooth' });
  });

  leiste.markiere = (pfad) => {
    const ziel = zielFuer(pfad);
    leiste.dataset.aktiv = ziel.pfad;
    for (const tab of leiste.querySelectorAll('.tab[data-pfad]')) {
      const treffer = tab.dataset.pfad === ziel.pfad;
      tab.classList.toggle('aktiv', treffer);
      if (treffer) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
    }
    // „Mehr" leuchtet mit, wenn das aktive Ziel dahinter liegt — sonst wirkt
    // die Leiste im Verorten-Reiter, als sei man nirgends.
    mehr.classList.toggle('aktiv', WEITERE_ZIELE.some((z) => z.pfad === ziel.pfad));
  };

  return leiste;
}
